import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import Require2FA from './pages/Require2FA';
import LogsViewer from './pages/LogsViewer';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Automation from './pages/Automation';
import Statistics from './pages/Statistics';
import Settings from './pages/Settings';
import About from './pages/About';
import Stations from './pages/Stations';
import UserAnnouncement from './pages/UserAnnouncement';
import CaseysPricing from './pages/CaseysPricing';
import CombinedFuelPricing from './pages/CombinedFuelPricing';
import MasterLocations from './pages/MasterLocations';
import PriceOverrides from './pages/PriceOverrides';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const isLocalBypassEnabled = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (!isLocal) return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get('bypassAdminAuth') === '1') {
    localStorage.setItem('adminBypassAuth', '1');
  } else if (params.get('bypassAdminAuth') === '0') {
    localStorage.removeItem('adminBypassAuth');
  }

  return localStorage.getItem('adminBypassAuth') === '1';
};

// Protected Route Component with 2FA check
const ProtectedRouteWith2FA = ({ token, children }) => {
  const navigate = useNavigate();
  const [twoFactorEnabled, setTwoFactorEnabled] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const check2FARef = React.useRef(false);

  React.useEffect(() => {
    // Prevent multiple simultaneous calls (React Strict Mode protection)
    if (check2FARef.current) return;
    
    check2FARef.current = true;
    const check2FA = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setTwoFactorEnabled(response.data.twoFactorEnabled);
      } catch (err) {
        console.error('Failed to check 2FA status:', err);
        // Handle rate limiting (429) - just set to false and continue
        if (err.response && err.response.status === 429) {
          console.warn('Rate limited on 2FA check, will retry on next navigation');
          setTwoFactorEnabled(false);
          setLoading(false);
          check2FARef.current = false;
          return;
        }
        // If token is expired or invalid (401/403), redirect to login
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('adminToken');
          navigate('/login', { replace: true });
          return;
        }
        setTwoFactorEnabled(false);
      } finally {
        setLoading(false);
        // Reset after delay for React Strict Mode
        setTimeout(() => {
          check2FARef.current = false;
        }, 1000);
      }
    };
    check2FA();
  }, [token, navigate]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>;
  }

  if (!twoFactorEnabled) {
    return <Navigate to="/require-2fa" replace />;
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, require2FA = false }) => {
  if (isLocalBypassEnabled()) {
    return children;
  }

  const token = localStorage.getItem('adminToken');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If 2FA is required, check if user has it enabled
  if (require2FA) {
    return <ProtectedRouteWith2FA token={token}>{children}</ProtectedRouteWith2FA>;
  }

  return children;
};

// Main Layout Component
const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL
    navigate(`/${page === 'dashboard' ? '' : page}`);
  };

  // Sync current page with URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') {
      setCurrentPage('dashboard');
    } else if (path === '/users') {
      setCurrentPage('users');
    } else if (path === '/logs') {
      setCurrentPage('logs');
    } else if (path === '/automation') {
      setCurrentPage('automation');
    } else if (path === '/statistics') {
      setCurrentPage('statistics');
    } else if (path === '/settings') {
      setCurrentPage('settings');
    } else if (path === '/about') {
      setCurrentPage('about');
    } else if (path === '/stations') {
      setCurrentPage('stations');
    } else if (path === '/app-announcement') {
      setCurrentPage('app-announcement');
    } else if (path === '/pricing-import') {
      setCurrentPage('pricing-import');
    } else if (path === '/fuel-prices-import') {
      setCurrentPage('fuel-prices-import');
    } else if (path === '/master-locations') {
      setCurrentPage('master-locations');
    } else if (path === '/price-overrides') {
      setCurrentPage('price-overrides');
    }
  }, [location.pathname]);

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'users':
        return <Users />;
      case 'logs':
        return <LogsViewer />;
      case 'automation':
        return <Automation />;
      case 'statistics':
        return <Statistics />;
      case 'settings':
        return <Settings />;
      case 'about':
        return <About />;
      case 'stations':
        return <Stations />;
      case 'app-announcement':
        return <UserAnnouncement />;
      case 'pricing-import':
        return <CaseysPricing />;
      case 'fuel-prices-import':
        return <CombinedFuelPricing />;
      case 'master-locations':
        return <MasterLocations />;
      case 'price-overrides':
        return <PriceOverrides />;
      default:
        return <Dashboard />;
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f6fa' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {/* Mobile Menu Button */}
      {isMobile && (
        <Button
          icon="pi pi-bars"
          onClick={() => setSidebarOpen(true)}
          className="p-button-text"
          style={{ position: 'fixed', top: '1rem', left: '1rem', zIndex: 1100 }}
        />
      )}

      {/* Main Content */}
      <div style={{ 
        flexGrow: 1, 
        width: isMobile ? '100%' : 'calc(100% - 240px)', 
        marginLeft: isMobile ? '0' : '240px',
        paddingTop: isMobile ? '4rem' : '1rem'
      }}>
        {renderPage()}
      </div>
    </div>
  );
};

function App() {
  try {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/require-2fa" element={<Require2FA />} />
          <Route
            path="/"
            element={
              <ProtectedRoute require2FA={true}>
                <MainLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute require2FA={true}>
                <MainLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute require2FA={true}>
                <MainLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    );
  } catch (error) {
    console.error('App render error:', error);
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Error loading application</h1>
        <p>{error.message}</p>
      </div>
    );
  }
}

export default App;
